import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, Sparkles, Building2, Users } from 'lucide-react';
import { compressLogo } from '@/lib/imageUtils';

const OnboardingProcessing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isComplete, setIsComplete] = React.useState(false);
  const [isSettingUpAccount, setIsSettingUpAccount] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const onboardingData = location.state as {
    action: 'join' | 'create';
    organizationId?: string;
    organizationName?: string;
    organizationColor?: string;
    organizationLogo?: File;
    selectedOrganization?: any;
    locationData?: {
      country: string;
      state: string;
      city: string;
      generatedTeamName: string;
    };
  };

  useEffect(() => {
    if (!onboardingData || !user) {
      navigate('/onboarding', { replace: true });
      return;
    }

    processOnboarding();
  }, [onboardingData, user]);

  // Simulate progress for better UX
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
    }, 200);

    return () => clearInterval(progressInterval);
  }, []);

  const processOnboarding = async () => {
    try {
      console.log('=== PROCESSING ONBOARDING ===');
      console.log('Action:', onboardingData.action);
      
      if (onboardingData.action === 'join') {
        // Join existing organization
        console.log('=== JOINING EXISTING ORGANIZATION ===');
        console.log('Organization to join:', onboardingData.selectedOrganization);
        
        // STEP 1: First, update profile with organization_id (without team_id yet)
        // This allows user to read teams from the organization via RLS
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('user_id', user.id)
          .single();

        if (existingProfile) {
          // Update existing profile with organization first
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              organization_id: onboardingData.selectedOrganization.id,
              onboarding_completed: true
            })
            .eq('user_id', user.id);

          if (profileError) {
            console.error('Error updating profile with organization:', profileError);
            throw profileError;
          }
        } else {
          // Create new profile with organization first
          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              user_id: user.id,
              organization_id: onboardingData.selectedOrganization.id,
              onboarding_completed: true
            });

          if (profileError) {
            console.error('Error creating profile with organization:', profileError);
            throw profileError;
          }
        }

        console.log('✅ Profile updated with organization_id, now user can read teams via RLS');
        
        // STEP 2: Now that user is part of organization, find or assign team
        let defaultTeam = null;
        let teamError = null;
        
        if (onboardingData.selectedOrganization.isFeaturedTeam && onboardingData.selectedOrganization.teamId) {
          // For featured teams, use the specific team ID
          console.log('🎯 Featured team detected, looking for team:', onboardingData.selectedOrganization.teamId);
          const { data: featuredTeam, error: featuredTeamError } = await supabase
            .from('teams')
            .select('id, name')
            .eq('id', onboardingData.selectedOrganization.teamId)
            .single();
          
          if (featuredTeamError) {
            console.error('❌ Error finding featured team:', featuredTeamError);
          } else {
            console.log('✅ Featured team found:', featuredTeam);
          }
          
          defaultTeam = featuredTeam;
          teamError = featuredTeamError;
        } else {
          // Get or create a default team for the organization
          const { data: orgTeam, error: orgTeamError } = await supabase
            .from('teams')
            .select('id, name')
            .eq('organization_id', onboardingData.selectedOrganization.id)
            .limit(1)
            .maybeSingle();
          
          defaultTeam = orgTeam;
          teamError = orgTeamError;
        }

        // If no team exists, create a default team
        if (!defaultTeam && !teamError) {
          console.log('No team found, creating default team for organization');
          const { data: newTeam, error: createTeamError } = await supabase
            .from('teams')
            .insert({
              name: `${onboardingData.selectedOrganization.name} - Hauptteam`,
              team_code: `${onboardingData.selectedOrganization.name.toUpperCase().replace(/\s+/g, '-')}-001`,
              organization_id: onboardingData.selectedOrganization.id,
              team_leader_id: user.id
            })
            .select('id, name')
            .single();

          if (createTeamError) {
            console.error('Error creating default team:', createTeamError);
          } else {
            defaultTeam = newTeam;
            console.log('Created default team:', defaultTeam);
          }
        }

        if (teamError && teamError.code !== 'PGRST116') {
          console.error('Error finding team:', teamError);
        }

        // STEP 3: Update profile with team_id
        if (defaultTeam) {
          console.log('📝 Updating profile with team_id:', defaultTeam.id);
          const { error: teamUpdateError } = await supabase
            .from('profiles')
            .update({
              team_id: defaultTeam.id
            })
            .eq('user_id', user.id);

          if (teamUpdateError) {
            console.error('❌ Error updating profile with team_id:', teamUpdateError);
            throw teamUpdateError;
          }
          
          console.log('✅ Profile successfully updated with team_id:', defaultTeam.id);
        } else {
          console.log('⚠️ No team assigned to user - will be assigned later');
        }
        
        console.log('✅ Successfully joined organization and team!');
        toast.success('Erfolgreich der Organisation beigetreten!');
        
      } else if (onboardingData.action === 'create') {
        // Create new organization
        console.log('=== CREATING NEW ORGANIZATION ===');
        console.log('Organization name:', onboardingData.organizationName);
        
        // Check if organization name already exists
        const { data: existingOrg, error: checkError } = await supabase
          .from('organizations')
          .select('id, name')
          .ilike('name', onboardingData.organizationName!)
          .limit(1);

        if (checkError) throw checkError;

        if (existingOrg && existingOrg.length > 0) {
          toast.error(`Eine Organisation mit dem Namen "${onboardingData.organizationName}" existiert bereits.`);
          navigate('/onboarding', { replace: true });
          return;
        }
        
        // Handle logo upload if provided
        let logoUrl = null;
        if (onboardingData.organizationLogo) {
          // Compress and resize logo before upload
          const compressedLogo = await compressLogo(onboardingData.organizationLogo);
          const fileExt = compressedLogo.name.split('.').pop();
          const fileName = `${crypto.randomUUID()}.${fileExt}`;
          
          const { error: uploadError, data: uploadData } = await supabase.storage
            .from('organization-logos')
            .upload(fileName, compressedLogo);
          
          if (uploadError) {
            console.error('Logo upload error:', uploadError);
          } else {
            logoUrl = uploadData.path;
          }
        }

        // Create new organization
        const isVodafone = onboardingData.organizationName!.toLowerCase().includes('vodafone');
        const organizationColor = onboardingData.organizationColor || (isVodafone ? '#E60000' : '#3B82F6');
        
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .insert({
            name: onboardingData.organizationName!,
            type: isVodafone ? 'vodafone' : 'custom',
            color: organizationColor,
            logo_url: logoUrl,
            created_by: user.id
          })
          .select()
          .single();

        if (orgError) {
          if (orgError.code === '23505') {
            toast.error(`Eine Organisation mit dem Namen "${onboardingData.organizationName}" existiert bereits.`);
            navigate('/onboarding', { replace: true });
            return;
          }
          throw orgError;
        }

        console.log('Organization created:', orgData);
        
        // Create a team for the organization
        const teamCode = onboardingData.locationData?.generatedTeamName || `${onboardingData.organizationName} Team`;
        const { data: teamData, error: teamError } = await supabase
          .from('teams')
          .insert({
            name: `${onboardingData.organizationName} Team`,
            team_code: teamCode,
            organization_id: orgData.id,
            team_leader_id: user.id
          })
          .select()
          .single();

        if (teamError) throw teamError;
        
        console.log('Team created:', teamData);
        
        // Check if profile exists first for organization creation
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('user_id', user.id)
          .single();

        if (existingProfile) {
          // Update existing profile
          const { error: profileError } = await supabase
            .from('profiles')
            .update({
              organization_id: orgData.id,
              team_id: teamData.id,
              onboarding_completed: true
            })
            .eq('user_id', user.id);

          if (profileError) throw profileError;
        } else {
          // Create new profile
          const { error: profileError } = await supabase
            .from('profiles')
            .insert({
              user_id: user.id,
              organization_id: orgData.id,
              team_id: teamData.id,
              onboarding_completed: true
            });

          if (profileError) throw profileError;
        }
        
        // Add admin role to user_roles table for organization creator
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: user.id,
            role: 'admin'
          });
        
        if (roleError) {
          console.error('Error adding admin role:', roleError);
          // Don't throw - profile update succeeded
        } else {
          console.log('Admin role added successfully');
        }
        
        console.log('Profile updated successfully');
        toast.success('Organisation erfolgreich erstellt!');
      }

      // Send welcome email after successful onboarding
      try {
        let teamNameForEmail = '';
        let orgNameForEmail = '';

        // Get current user profile to fetch team information
        const { data: profileData } = await supabase
          .from('profiles')
          .select('team_id, organization_id')
          .eq('user_id', user.id)
          .single();

        if (profileData?.team_id) {
          const { data: teamData } = await supabase
            .from('teams')
            .select('name')
            .eq('id', profileData.team_id)
            .single();
          
          teamNameForEmail = teamData?.name || 'Ihr Team';
        }

        if (profileData?.organization_id) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', profileData.organization_id)
            .single();
          
          orgNameForEmail = orgData?.name || 'Ihre Organisation';
        }

        // Fallback values
        if (!teamNameForEmail) {
          teamNameForEmail = onboardingData.action === 'create' 
            ? `${onboardingData.organizationName} Team` 
            : 'Ihr Team';
        }
        
        if (!orgNameForEmail) {
          orgNameForEmail = onboardingData.action === 'create'
            ? onboardingData.organizationName || 'Ihre Organisation'
            : onboardingData.selectedOrganization?.name || 'Ihre Organisation';
        }

        await supabase.functions.invoke('send-onboarding-welcome', {
          body: {
            userId: user.id,
            teamName: teamNameForEmail,
            organizationName: orgNameForEmail
          }
        });
        
        console.log('Welcome email sent successfully');
      } catch (emailError) {
        console.error('Error sending welcome email:', emailError);
        // Don't throw - onboarding was successful
      }
      
      // Complete progress and show success
      setProgress(100);
      setIsComplete(true);
      
      // Show account setup message
      setTimeout(() => {
        setIsSettingUpAccount(true);
      }, 1000);
      
      // Add a longer delay to ensure database changes are propagated and realtime updates are received
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Clear any onboarding data from localStorage to prevent issues
      localStorage.removeItem('onboarding-data');
      localStorage.removeItem('onboarding-step');
      
      // Navigate to homepage after brief delay
      setTimeout(() => {
        console.log('OnboardingProcessing: Navigating to homepage');
        // Force a full page reload to ensure clean state
        window.location.href = '/';
      }, 2500);
      
    } catch (error: any) {
      console.error('Error processing onboarding:', error);
      toast.error('Fehler beim Verarbeiten: ' + error.message);
      setTimeout(() => {
        navigate('/onboarding', { replace: true });
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="shadow-2xl border-0 bg-card/90 backdrop-blur-sm animate-fade-in">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 relative">
              {isComplete ? (
                <div className="relative">
                  <CheckCircle className="h-20 w-20 text-emerald-500 animate-scale-in" />
                  <Sparkles className="absolute -top-2 -right-2 h-8 w-8 text-yellow-400 animate-pulse" />
                </div>
              ) : (
                <div className="relative">
                  {onboardingData?.action === 'create' ? (
                    <Building2 className="h-20 w-20 text-primary animate-pulse" />
                  ) : (
                    <Users className="h-20 w-20 text-primary animate-pulse" />
                  )}
                  <Loader2 className="absolute -bottom-1 -right-1 h-8 w-8 text-secondary animate-spin" />
                </div>
              )}
            </div>
            
            <CardTitle className="text-3xl bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent font-bold">
              {isComplete ? 'Willkommen! 🎉' : 'Setup läuft...'}
            </CardTitle>
            
            <CardDescription className="text-lg">
              {isComplete 
                ? 'Ihr Konto wurde erfolgreich eingerichtet' 
                : 'Wir richten alles für Sie ein'
              }
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8">
            <div className="space-y-8">
              {/* Progress Bar */}
              <div className="space-y-3">
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-muted-foreground">Fortschritt</span>
                  <span className="text-primary">{progress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

               {/* Status Message */}
               <div className="text-center space-y-4">
                 {isSettingUpAccount ? (
                   <div className="space-y-3 animate-fade-in">
                     <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-medium">
                       <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                       Konto wird eingerichtet...
                     </div>
                     <p className="text-muted-foreground">
                       Bereitet Ihren Zugang vor...
                     </p>
                   </div>
                 ) : isComplete ? (
                   <div className="space-y-3 animate-fade-in">
                     <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                       <CheckCircle className="h-5 w-5 mr-2" />
                       Setup abgeschlossen!
                     </div>
                     <p className="text-muted-foreground">
                       Sie werden automatisch zur Startseite weitergeleitet...
                     </p>
                   </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary font-medium">
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {onboardingData?.action === 'join' ? 'Beitritt läuft...' : 'Erstellung läuft...'}
                      </div>
                      
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {onboardingData?.action === 'join' 
                          ? `Tritt der Organisation "${onboardingData.selectedOrganization?.name}" bei...`
                          : `Erstellt Organisation "${onboardingData?.organizationName}"...`
                        }
                      </p>
                    </div>

                    {/* Animated dots */}
                    <div className="flex justify-center space-x-1">
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Feature Preview */}
              {!isComplete && (
                <div className="bg-muted/50 rounded-lg p-4 animate-fade-in" style={{ animationDelay: '1s' }}>
                  <h4 className="font-semibold text-sm text-foreground mb-2">Was Sie als nächstes erwartet:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Intuitive Todo-Verwaltung</li>
                    <li>• Team-Zusammenarbeit</li>
                    <li>• Performance-Tracking</li>
                    <li>• Und vieles mehr...</li>
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingProcessing;